import { ADMIN_API_PREFIX, fillPath, route } from "./routes.js";
import { z } from "zod";
import { isLowerCaseUuid, listMachinePrincipalsQuerySchema, machinePrincipalPageSchema, machinePrincipalRecordSchema, updateMachinePrincipalRequestSchema } from "@zion/sdk-contracts";
//#region src/principals.ts
/**
* `/v1/admin/principals`: the agent, worker, service and external machine principals of the
* caller's org, as the org admin app lists, reads, updates and revokes them (#3682,
* admin-redesign-asks C-4, D-4, D-7, D-9).
*
* Producer: core-api serves the two `internal/v1/machine-principals` controllers at this second
* path, `MachinePrincipalAdminController` (list, read, patch) and the revoke route of
* `MachinePrincipalsController` (WS-5). Each route keeps the guard of its `internal/v1` twin:
* `MachinePrincipalAdminAuthGuard` on list, read and patch, `MachineRevokeAuthGuard` on revoke.
* There is no new permission: the caller acts on a principal it holds `machine_principal:manage`
* on, an org owner on every non-human principal through the standing rule (D-4). Consumer: the org
* admin app's census-driven client. The path sits under `/v1/admin`, so the shell's
* `createAppFetch` sends the viewer's session bearer, and the grant check judges the viewer.
*
* Shared rules of the four routes, as core-api answers them:
*
* - The route guard answers first: 403 for an MCP session token, and 403 for a request that
*   carries `x-zion-on-behalf-of`. A session bearer with that header gets 401 earlier, from the
*   global `JwtOrOnBehalfOfGuard`.
* - Every read runs under the caller's org with RLS, so another org's principal is 404.
* - A `human` principal is on no page, and its id answers 404 on `GET`, `PATCH` and the revoke
*   route of `{id}`, before the 403 of the manage check (ruling R-001).
* - `{id}` is a lower-case UUID; any other form is 400 before any read.
*
* This family is a second door to the `internal/v1` routes, and is distinct from
* `/v1/admin/machine-principals` (`machine-principals.ts`), which serves `external` principals
* only under `settings:identity:manage`.
*
* Shapes: the request and response schemas are the machine-principal admin schemas of
* `@zion/sdk-contracts`, imported and re-exported by identity under this family's names, so the
* two doors can never answer different shapes. The one shape `@zion/sdk-contracts` does not hold
* is the revoke body, {@link revokeAdminPrincipalRequestSchema}.
*
* Direction rule (R-28): the response schemas are `z.object`, the request schemas are
* `z.strictObject`. No schema here carries a `.transform()`, so each one stays representable for
* `createOpenApiDocument()`. This module does no I/O and holds no state.
*/
/** The collection path. No parameter, so the OpenAPI form and the request form are the same. */
const ADMIN_PRINCIPALS_PATH = `${ADMIN_API_PREFIX}/principals`;
/** One principal, in OpenAPI form. Fill it with {@link adminPrincipalPath}. */
const ADMIN_PRINCIPAL_PATH_TEMPLATE = `${ADMIN_PRINCIPALS_PATH}/{id}`;
/** Revoke one principal, in OpenAPI form. Fill it with {@link adminPrincipalRevokePath}. */
const ADMIN_PRINCIPAL_REVOKE_PATH_TEMPLATE = `${ADMIN_PRINCIPAL_PATH_TEMPLATE}/revoke`;
/** The longest `reason` a revoke body may carry, the bound core-api's revoke body sets. */
const ADMIN_PRINCIPAL_REVOKE_REASON_MAX_LENGTH = 512;
/**
* `GET /v1/admin/principals`. One page of the non-human principals of the caller's org that the
* caller manages, narrowed by {@link listAdminPrincipalsQuerySchema} (`type`, `status`, `agentId`,
* `appSlug`, `cursor`, `limit`), in principal-id order. Status 200 with
* {@link adminPrincipalPageSchema}. A caller that manages none gets 200 and an empty page. The
* `appSlug` filter matches the derived slug the record's `appSlug` shows. 400 on a query the schema
* refuses or a cursor core-api cannot decode; 403 for an on-behalf-of request or an MCP session
* token.
*/
const LIST_ADMIN_PRINCIPALS_ROUTE = route("GET", ADMIN_PRINCIPALS_PATH);
/**
* `GET /v1/admin/principals/{id}`. One principal, 200 with {@link adminPrincipalResponseSchema}.
* 400 for an id that is not a lower-case UUID; 404 for a missing principal, one of another org,
* or a `human` principal, before the 403 of the manage check; 403 when the caller does not manage
* it, or for an on-behalf-of request or an MCP session token.
*/
const GET_ADMIN_PRINCIPAL_ROUTE = route("GET", ADMIN_PRINCIPAL_PATH_TEMPLATE);
/**
* `PATCH /v1/admin/principals/{id}`. Applies {@link updateAdminPrincipalRequestSchema} and answers
* 200 with the saved {@link adminPrincipalResponseSchema}. Every status of the `internal/v1` twin
* applies (`UPDATE_MACHINE_PRINCIPAL_ROUTE` in `@zion/sdk-contracts`): the `GET` statuses, 400 for
* a refused body or an operator-only attribute key, 403 for a role set or a suspend the rules
* refuse, and 409 for a `revoked` principal.
*/
const UPDATE_ADMIN_PRINCIPAL_ROUTE = route("PATCH", ADMIN_PRINCIPAL_PATH_TEMPLATE);
/**
* `POST /v1/admin/principals/{id}/revoke`. Revokes the principal with
* {@link revokeAdminPrincipalRequestSchema}; `revoked` is final. Status 204 and no body. 400 for an
* id that is not a lower-case UUID, a body the schema refuses, or a body `orgId` that is not the caller's
* org; 403 for an on-behalf-of request, an MCP session token, or a caller that may not manage
* the principal; 404 for a principal the caller's org cannot see, or a `human` principal, before
* the 403 of the manage check and before any write.
*
* A `human` id is hidden on this door for every caller, the org revoke key and the site-wide
* grant included (ruling R-001). The `internal/v1` twin keeps its own answer for a `human` id.
*/
const REVOKE_ADMIN_PRINCIPAL_ROUTE = route("POST", ADMIN_PRINCIPAL_REVOKE_PATH_TEMPLATE);
/** The list query: `listMachinePrincipalsQuerySchema` of `@zion/sdk-contracts`, by identity. */
const listAdminPrincipalsQuerySchema = listMachinePrincipalsQuerySchema;
/** One principal: `machinePrincipalRecordSchema` of `@zion/sdk-contracts`, by identity. */
const adminPrincipalSchema = machinePrincipalRecordSchema;
/** The list body: `machinePrincipalPageSchema` of `@zion/sdk-contracts`, by identity. */
const adminPrincipalPageSchema = machinePrincipalPageSchema;
/** The `GET` and `PATCH` body: one principal, {@link adminPrincipalSchema}. */
const adminPrincipalResponseSchema = machinePrincipalRecordSchema;
/** The `PATCH` body: `updateMachinePrincipalRequestSchema` of `@zion/sdk-contracts`, by identity. */
const updateAdminPrincipalRequestSchema = updateMachinePrincipalRequestSchema;
/**
* The revoke body: `orgId`, the caller's org id as a UUID, and an optional `reason` of at most
* {@link ADMIN_PRINCIPAL_REVOKE_REASON_MAX_LENGTH} characters that core-api writes to the audit
* trail. Strict: an unknown key is refused, as the server's strict pipe refuses it. core-api
* answers 400 when `orgId` is not the caller's org, so the body can name no other org.
*/
const revokeAdminPrincipalRequestSchema = z.strictObject({
	orgId: z.uuid(),
	reason: z.string().max(512).optional()
});
/** The id schema every builder here validates a principal id with: the form core-api admits. */
const principalIdParamSchema = z.string().refine(isLowerCaseUuid, { message: "a principal id is a lower-case UUID" });
/**
* Fill {@link ADMIN_PRINCIPAL_PATH_TEMPLATE}, the path of `GET` and `PATCH`.
*
* @param principalId the principal id, as {@link adminPrincipalSchema} `principalId` publishes it.
* @throws a `ZodError` when `principalId` is not a lower-case UUID, so a refused id never reaches
*   a path: an upper-case UUID, an agent slug, `..`, `/` and the empty string included.
*/
function adminPrincipalPath(principalId) {
	return fillPath(ADMIN_PRINCIPAL_PATH_TEMPLATE, { id: principalId }, { id: principalIdParamSchema });
}
/**
* Fill {@link ADMIN_PRINCIPAL_REVOKE_PATH_TEMPLATE}.
*
* @throws a `ZodError` for the ids {@link adminPrincipalPath} refuses.
*/
function adminPrincipalRevokePath(principalId) {
	return fillPath(ADMIN_PRINCIPAL_REVOKE_PATH_TEMPLATE, { id: principalId }, { id: principalIdParamSchema });
}
/** Every route this file declares, for `route-census.ts`, as its `principals` family. */
const principalsRoutes = Object.freeze([
	LIST_ADMIN_PRINCIPALS_ROUTE,
	GET_ADMIN_PRINCIPAL_ROUTE,
	UPDATE_ADMIN_PRINCIPAL_ROUTE,
	REVOKE_ADMIN_PRINCIPAL_ROUTE
]);
//#endregion
export { ADMIN_PRINCIPALS_PATH, ADMIN_PRINCIPAL_PATH_TEMPLATE, ADMIN_PRINCIPAL_REVOKE_PATH_TEMPLATE, ADMIN_PRINCIPAL_REVOKE_REASON_MAX_LENGTH, GET_ADMIN_PRINCIPAL_ROUTE, LIST_ADMIN_PRINCIPALS_ROUTE, REVOKE_ADMIN_PRINCIPAL_ROUTE, UPDATE_ADMIN_PRINCIPAL_ROUTE, adminPrincipalPageSchema, adminPrincipalPath, adminPrincipalResponseSchema, adminPrincipalRevokePath, adminPrincipalSchema, listAdminPrincipalsQuerySchema, principalsRoutes, revokeAdminPrincipalRequestSchema, updateAdminPrincipalRequestSchema };

//# sourceMappingURL=principals.js.map